import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner, Alert } from "react-bootstrap";
import { Search, Printer, User, BookOpen, MapPin, Calendar, CheckCircle, Eye, ZoomIn, ZoomOut, Maximize2, Minimize2, X, ArrowLeft } from "lucide-react";
import api from "../api";
import puentesLogo from "../assets/img/puentes-logo.png";
import utnLogo from "../assets/img/utn-logo.png";
import signatureYacoy from "../assets/img/signature_yacoy.png";

const RegularStudentCertificateModal = ({ onClose }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [diplomas, setDiplomas] = useState([]);
  const [selectedDiploma, setSelectedDiploma] = useState(null);
  const [municipality, setMunicipality] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [showPreview, setShowPreview] = useState(false);
  const [zoom, setZoom] = useState(0.8);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Data update states
  const [editDni, setEditDni] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const res = await api.get("users/students/");
      setStudents(res.data);
    } catch (err) {
      console.error("Error fetching students:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelect = async (student) => {
    setSelectedStudent(student);
    setEditDni(student.profile?.dni || "");
    setEditFirstName(student.first_name || "");
    setEditLastName(student.last_name || "");
    setSearchTerm("");
    
    try {
      const res = await api.get("diplomas/");
      setDiplomas(res.data);
      if (student.municipio) setMunicipality(student.municipio);
    } catch (err) {
      console.error("Error fetching diplomas:", err);
    }
  };

  const handleUpdateAndPreview = async () => {
    if (!editDni || !editFirstName || !editLastName) {
      setUpdateError("Todos los campos personales son obligatorios para el certificado.");
      return;
    }

    setIsUpdating(true);
    setUpdateError("");
    try {
      await api.patch(`users/${selectedStudent.id}/update_student_data/`, {
        dni: editDni,
        first_name: editFirstName,
        last_name: editLastName
      });
      
      // Update local state
      setSelectedStudent({
        ...selectedStudent,
        first_name: editFirstName,
        last_name: editLastName,
        profile: { ...selectedStudent.profile, dni: editDni }
      });
      
      // Separado en dos commits: ver nota equivalente en
      // CustomCertificateModal.jsx handleUpdateAndPreview.
      setIsUpdating(false);
      await new Promise((resolve) => setTimeout(resolve, 0));
      setShowPreview(true);
    } catch (err) {
      setUpdateError("Error al actualizar los datos del alumno.");
      console.error(err);
      setIsUpdating(false);
    }
  };

  const filteredStudents = students.filter(s => 
    s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.first_name && s.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.last_name && s.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.profile?.dni && s.profile.dni.includes(searchTerm))
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T12:00:00");
    const months = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    return `${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Modal 
      show={true} 
      onHide={onClose} 
      size={isExpanded ? undefined : "xl"}
      fullscreen={isExpanded}
      centered={!isExpanded}
      backdrop="static"
      className={`certificate-main-modal ${showPreview ? "preview-active" : ""}`}
    >
      <Modal.Header closeButton className={`border-0 pb-0 ${showPreview ? 'bg-light py-2' : 'glassmorphism-header'}`}>
        <Modal.Title className="w-100">
          <div key={showPreview ? "title-preview" : "title-form"}>
            {!showPreview ? (
              <div className="d-flex align-items-center gap-2">
                <div className="p-2 bg-success bg-opacity-10 rounded-circle text-success">
                  <CheckCircle size={24} />
                </div>
                <div>
                  <h5 className="mb-0 fw-bold">Generar Constancia de Alumno Regular</h5>
                  <small className="text-muted fw-normal">Completa los datos para el certificado</small>
                </div>
              </div>
            ) : (
              <div className="d-flex align-items-center justify-content-between w-100 pe-4">
                <span className="fw-bold text-primary fs-5">Vista Previa de Constancia</span>
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex align-items-center gap-1 bg-white border rounded px-2 py-1 shadow-sm mx-3">
                    <Button variant="link" className="p-0 text-muted" onClick={() => setZoom(Math.max(0.3, zoom - 0.1))}><ZoomOut size={16} /></Button>
                    <span className="small fw-bold text-muted mx-1" style={{ minWidth: '40px', textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
                    <Button variant="link" className="p-0 text-muted" onClick={() => setZoom(Math.min(1.5, zoom + 0.1))}><ZoomIn size={16} /></Button>
                  </div>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="d-flex align-items-center justify-content-center p-1"
                    title={isExpanded ? "Contraer" : "Pantalla Completa"}
                    style={{ width: '32px', height: '32px' }}
                  >
                    {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className={showPreview ? "p-0 bg-dark bg-opacity-75 d-flex justify-content-center overflow-auto preview-body" : "p-4 form-body"} style={showPreview ? { height: isExpanded ? 'calc(100vh - 120px)' : '80vh' } : {}}>
        <div key={showPreview ? "preview-container" : "form-container"} className="w-100 h-100">
          {!showPreview ? (
            /* FORMULARIO DE DATOS */
            <div className="certificate-form-content">
              {!selectedStudent ? (
                <div className="search-section animate-fade-in">
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold small text-uppercase text-muted">1. Buscar Alumno</Form.Label>
                    <div className="position-relative">
                      <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={18} />
                      <Form.Control
                        type="text"
                        placeholder="Buscar por nombre, email o DNI..."
                        className="ps-5 py-3 shadow-sm border-0 bg-light"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                  </Form.Group>

                  <div className="student-list border rounded shadow-sm bg-white overflow-auto" style={{ maxHeight: '350px' }}>
                    {loading ? (
                      <div className="p-5 text-center">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">Cargando alumnos...</p>
                      </div>
                    ) : filteredStudents.length > 0 ? (
                      filteredStudents.map(student => (
                        <div 
                          key={student.id} 
                          className="p-3 border-bottom list-item-hover cursor-pointer d-flex align-items-center justify-content-between"
                          onClick={() => handleStudentSelect(student)}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <div className="p-2 bg-primary bg-opacity-10 rounded-circle text-primary">
                              <User size={20} />
                            </div>
                            <div>
                              <div className="fw-bold">{student.first_name} {student.last_name}</div>
                              <div className="small text-muted">{student.email} • DNI: {student.profile?.dni || "Faltante"}</div>
                            </div>
                          </div>
                          <Button variant="outline-primary" size="sm">Seleccionar</Button>
                        </div>
                      ))
                    ) : (
                      <div className="p-5 text-center text-muted">
                        No se encontraron alumnos con ese criterio.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="form-section animate-fade-in">
                  <div className="selected-student-badge p-3 bg-light rounded mb-4 d-flex align-items-center justify-content-between border">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 bg-primary bg-opacity-10 rounded-circle text-primary">
                        <User size={20} />
                      </div>
                      <div>
                        <div className="fw-bold">{selectedStudent.first_name} {selectedStudent.last_name}</div>
                        <div className="small text-muted">{selectedStudent.email}</div>
                      </div>
                    </div>
                    <Button variant="link" size="sm" onClick={() => setSelectedStudent(null)} className="text-decoration-none">
                      Cambiar alumno
                    </Button>
                  </div>

                  {updateError && <Alert variant="danger">{updateError}</Alert>}

                  <Row className="g-4">
                    <Col md={12}>
                      <div className="p-3 bg-info bg-opacity-10 rounded border border-info mb-2">
                        <h6 className="fw-bold text-info mb-3 d-flex align-items-center gap-2">
                          <User size={18} /> Validar Datos Personales
                        </h6>
                        <Row className="g-3">
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Nombre</Form.Label>
                            <Form.Control value={editFirstName} onChange={e => setEditFirstName(e.target.value)} placeholder="Nombre" />
                          </Col>
                          <Col md={4}>
                            <Form.Label className="small fw-bold">Apellido</Form.Label>
                            <Form.Control value={editLastName} onChange={e => setEditLastName(e.target.value)} placeholder="Apellido" />
                          </Col>
                          <Col md={4}>
                            <Form.Label className="small fw-bold text-danger">DNI (Requerido)</Form.Label>
                            <Form.Control value={editDni} onChange={e => setEditDni(e.target.value)} placeholder="DNI sin puntos" className={!editDni ? "border-danger" : ""} />
                          </Col>
                        </Row>
                        <small className="text-muted d-block mt-2">Estos datos se guardarán permanentemente en el perfil del alumno.</small>
                      </div>
                    </Col>

                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-bold small text-uppercase text-muted d-flex align-items-center gap-2">
                          <BookOpen size={16} /> 2. Seleccionar Diplomatura
                        </Form.Label>
                        <Form.Select 
                          className="py-3 shadow-sm border-0 bg-light"
                          onChange={(e) => setSelectedDiploma(diplomas.find(d => d.id == e.target.value))}
                          value={selectedDiploma?.id || ""}
                        >
                          <option value="">Selecciona una diplomatura...</option>
                          {diplomas.map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold small text-uppercase text-muted d-flex align-items-center gap-2">
                          <MapPin size={16} /> 3. Municipio
                        </Form.Label>
                        <Form.Control 
                          type="text"
                          className="py-3 shadow-sm border-0 bg-light"
                          placeholder="Ej: Mercedes"
                          value={municipality}
                          onChange={(e) => setMunicipality(e.target.value)}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold small text-uppercase text-muted d-flex align-items-center gap-2">
                          <Calendar size={16} /> 4. Fecha de Emisión
                        </Form.Label>
                        <Form.Control 
                          type="date"
                          className="py-3 shadow-sm border-0 bg-light"
                          value={date}
                          onChange={(e) => setDate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mt-5 d-flex gap-2">
                    <Button 
                      variant="primary" 
                      className="w-100 py-3 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                      disabled={!selectedDiploma || !municipality || !editDni || isUpdating}
                      onClick={handleUpdateAndPreview}
                    >
                      {isUpdating ? <Spinner animation="border" size="sm" /> : <Eye size={20} />}
                      {isUpdating ? "Guardando datos..." : "Previsualizar y Guardar"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* VISTA PREVIA DEL CERTIFICADO */
            <div className="d-flex justify-content-center w-100 pb-5 h-100">
              <div 
                id="certificate-to-print" 
                className="certificate-page shadow-lg my-4 bg-white" 
                style={{
                  width: '210mm',
                  height: '297mm',
                  backgroundColor: 'white',
                  color: 'black',
                  fontFamily: "'Times New Roman', Times, serif",
                  position: 'relative',
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top center',
                  padding: '25mm',
                  boxSizing: 'border-box',
                  overflow: 'hidden'
                }}
              >
                {/* Header Logos */}
                <div className="d-flex justify-content-between align-items-start mb-5">
                  <div className="d-flex flex-column align-items-center" style={{ width: '200px' }}>
                    <img src={puentesLogo} alt="Logo Puentes" style={{ width: '130px', marginBottom: '10px' }} />
                    <div className="text-center" style={{ fontSize: '11px', fontWeight: 'bold', lineHeight: '1.2' }}>
                      PUENTES<br/>
                      PLAN DE INTEGRACIÓN<br/>
                      TERRITORIAL<br/>
                      UNIVERSITARIA
                    </div>
                  </div>
                  <div className="d-flex flex-column align-items-center" style={{ width: '220px' }}>
                    <img src={utnLogo} alt="Logo UTN" style={{ width: '80px', marginBottom: '10px' }} />
                    <div className="text-center" style={{ fontSize: '11px', fontWeight: 'bold', lineHeight: '1.2' }}>
                      UNIVERSIDAD TECNOLÓGICA NACIONAL<br/>
                      DIPLOMATURAS {new Date().getFullYear()}
                    </div>
                  </div>
                </div>

                <div className="text-center mt-5 mb-5 pt-4">
                  <h1 className="fw-bold" style={{ fontSize: '26px', textDecoration: 'underline' }}>CONSTANCIA DE ALUMNO REGULAR</h1>
                </div>

                <div className="content mt-5 pt-4 text-justify" style={{ fontSize: '20px', lineHeight: '1.8', textAlign: 'justify' }}>
                  <p>
                    Se deja constancia que el/la Señor/a <strong>{(selectedStudent.first_name + " " + selectedStudent.last_name).toUpperCase()}</strong>, 
                    DNI: <strong>{selectedStudent.profile?.dni || "________"}</strong>, es alumno regular de la Diplomatura en: 
                    <strong> {selectedDiploma?.name.toUpperCase()}</strong> dependiente de Universidad Tecnológica Nacional, 
                    a través del Plan de Integración Territorial de la Provincia de Buenos Aires (PROGRAMA PUENTES) 
                    dictada en el Municipio de: <strong>{municipality.toUpperCase() || "________"}</strong>.
                  </p>
                  <div className="mt-5">
                    <p style={{ textAlign: 'justify', marginBottom: '0' }}>
                      Se extiende el presente certificado a solicitud del/la interesado/a, a solo efecto de ser presentado 
                      ante quien corresponda. La presente constancia tiene una validez de 30 días una vez emitida la misma.
                    </p>
                    <div style={{ width: '100%', borderBottom: '2px dashed #000', marginTop: '15px' }}></div>
                  </div>
                </div>

                <div className="footer-date mt-5 pt-5 text-end" style={{ fontSize: '20px', paddingRight: '15mm' }}>
                  <p>{municipality.toUpperCase()}, {formatDate(date)}</p>
                </div>

                <div className="signature-area d-flex flex-column align-items-center" style={{ position: 'absolute', bottom: '60px', left: '0', right: '0' }}>
                  <div style={{ width: '350px', textAlign: 'center' }}>
                    <img src={signatureYacoy} alt="Firma Lucia Yacoy" style={{ width: '320px', marginBottom: '-10px' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-light border-0">
        <div key={showPreview ? "footer-preview" : "footer-form"} className="w-100 d-flex gap-2">
          {!showPreview ? (
            <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          ) : (
            <>
              <Button variant="outline-secondary" onClick={() => setShowPreview(false)} className="d-flex align-items-center gap-2">
                <ArrowLeft size={18} /> Volver a Editar
              </Button>
              <Button variant="primary" size="lg" onClick={handlePrint} className="d-flex align-items-center gap-2 px-4 fw-bold shadow-sm ms-auto">
                <Printer size={20} /> Imprimir Constancia
              </Button>
            </>
          )}
        </div>
      </Modal.Footer>

      <style>{`
        .list-item-hover:hover {
          background-color: #f8f9fa;
        }
        .certificate-main-modal.preview-active .modal-content {
          background: #222 !important;
        }
        
        .certificate-page strong {
          font-weight: 700;
        }
        @page {
          size: A4;
          margin: 0 !important;
        }
        @media print {
          body {
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact;
          }
          #certificate-to-print {
            transform: none !important;
            margin: 0 !important;
            box-shadow: none !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            visibility: visible !important;
            width: 210mm !important;
            height: 297mm !important;
          }
          body > *:not(.modal), .modal-header, .modal-footer, .modal-backdrop {
            display: none !important;
          }
          .modal {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            display: block !important;
          }
          .modal-dialog {
            margin: 0 !important;
            max-width: 100% !important;
          }
          .modal-content {
            border: none !important;
            background: white !important;
          }
          .modal-body {
            padding: 0 !important;
            overflow: visible !important;
          }
        }
      `}</style>
    </Modal>
  );
};

export default RegularStudentCertificateModal;
